import React, { JSX } from 'react'
import { Image, ImageStyle, View } from 'react-native'
import { Icon, Text, UI_ICONS_MAPPING } from '@components/base'
import { StyleService, useStyleSheet } from '@style/service.ts'
import { useIsDarkMode } from '@config/theme.ts'
import { UiIcon } from '@src/types'

type Benefit = {
  title: string
  description: string
} & (
  | {
      getImage: (
        isDarkMode: boolean,
      ) =>
        | {
            imageSource: any
          }
        | JSX.Element
      width: number
      height: number
    }
  | { icon: UiIcon }
)

interface BenefitCardProps {
  benefit: Benefit
  benefits: Benefit[]
}

const DEFAULT_ICON_SIZE = 50

export const BenefitCard = ({ benefit, benefits }: BenefitCardProps) => {
  const { title, description } = benefit
  const styles = useStyleSheet(themedStyle)
  const isDarkMode = useIsDarkMode()

  const maxWidth = Math.max(
    ...benefits.map((item) => {
      if ('icon' in item) {
        return DEFAULT_ICON_SIZE
      } else {
        const { width } = item
        return width
      }
    }),
  )

  const renderImageOrIcon = () => {
    if ('icon' in benefit) {
      const iconName = UI_ICONS_MAPPING[benefit.icon]
      if (!iconName) {
        return null
      }
      return <Icon name={iconName} size={DEFAULT_ICON_SIZE} />
    } else {
      const { width, height, getImage } = benefit
      const image = getImage(isDarkMode)

      if ('imageSource' in image) {
        return (
          <View style={[styles.imageContainer, { width: maxWidth }]}>
            <Image
              style={[styles.image as ImageStyle, { height, width }]}
              source={image.imageSource}
            />
          </View>
        )
      }

      return <View style={[styles.imageContainer, { width: maxWidth, height }]}>{image}</View>
    }
  }

  return (
    <View style={[styles.card]}>
      {renderImageOrIcon()}
      <View style={styles.textContainer}>
        <Text type="regular" bold>
          {title}
        </Text>
        <Text type="small" style={styles.secondaryText}>
          {description}
        </Text>
      </View>
    </View>
  )
}

const themedStyle = StyleService.create({
  card: {
    flexDirection: 'row',
    borderColor: 'theme.surface.base',
    backgroundColor: 'theme.surface.base2',
    alignItems: 'center',
    borderRadius: 24,
    borderWidth: 2,
    marginVertical: 4,
    paddingHorizontal: 16,
    gap: 16,
  },
  secondaryText: {
    marginTop: 4,
    color: 'theme.text.secondary',
  },
  image: {
    width: '100%',
    resizeMode: 'contain',
  },
  textContainer: {
    flex: 1,
    paddingVertical: 16,
  },
  imageContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
})
